import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, InjectionToken, Injector, StaticProvider, Type, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { TemplateModalOverlayRef } from './modal.component';

export interface ModalPosition {
  top?: string;
  left?: string;
  right?: string;
  bottom?: string;
}

export interface ModalConfig {
  disableClose?: boolean;
  width?: string;
  height?: string;
  minWidth?: string;
  minHeight?: string;
  maxWidth?: string;
  maxHeight?: string;
  position?: ModalPosition;
  data?: object;
  topClose?: boolean;
  hideCloseButton?: boolean;
}

export interface ModalRef<T = unknown> {
  id: string;
  componentInstance?: T;
  close(result?: unknown): void;
  afterClosed(): Observable<unknown>;
  afterOpened(): Observable<void>;
}

const DEFAULT_CONFIG: ModalConfig = {
  disableClose: false,
  topClose: true,
  hideCloseButton: false
};

export const MODAL_DATA = new InjectionToken<object>('MODAL_DATA');
export const MODAL_REF = new InjectionToken<ModalRef>('MODAL_REF');
export const MODAL_CONFIG = new InjectionToken<ModalConfig>('MODAL_CONFIG');

@Injectable({
  providedIn: 'root'
})
export class TemplateModalService {
  private readonly injector = inject(Injector);
  private readonly overlay = inject(Overlay);

  private readonly openModals = new Map<string, ModalRef>();
  private modalIdCounter = 0;

  openComponent<T>(component: Type<T>, config: ModalConfig = {}): ModalRef<T> {
    const modalId = `modal-${++this.modalIdCounter}`;
    const mergedConfig = { ...DEFAULT_CONFIG, ...config };

    const overlayRef = this.createOverlay();
    const modalRef = new TemplateModalOverlayRef(overlayRef, modalId);

    const injector = this.createInjector(mergedConfig, modalRef);
    const portal = new ComponentPortal(component, null, injector);
    const componentRef = overlayRef.attach(portal);
    modalRef.componentInstance = componentRef.instance;

    modalRef.afterClosed().subscribe(() => {
      this.openModals.delete(modalRef.id);
    });

    this.openModals.set(modalId, modalRef);
    return modalRef as ModalRef<T>;
  }

  private createInjector(config: ModalConfig, modalRef: TemplateModalOverlayRef): Injector {
    const providers: StaticProvider[] = [
      { provide: TemplateModalOverlayRef, useValue: modalRef },
      { provide: MODAL_REF, useValue: modalRef },
      { provide: MODAL_CONFIG, useValue: config }
    ];

    if (config.data !== undefined) {
      providers.push({ provide: MODAL_DATA, useValue: config.data });
    }

    return Injector.create({
      providers,
      parent: this.injector
    });
  }

  private createOverlay() {
    const positionStrategy = this.overlay.position()
      .global()
      .centerHorizontally()
      .centerVertically();

    const overlayConfig = new OverlayConfig({
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy,
      disposeOnNavigation: true
    });

    return this.overlay.create(overlayConfig);
  }
}
